import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
    ScrollView,
    Modal,
} from 'react-native';
import { createPortal } from 'react-dom';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import Checkbox from 'expo-checkbox';
import { sensorState, useScale, ScaleFn , useDarkMode, TablePagination} from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';
import moment from 'moment';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// 스크롤 가능한 영역 안에서 발생한 이벤트인지 판별 (모달 내부 드롭다운 등)
const isInScrollable = (target: any) => {
    let n: any = target;
    while (n && n !== document.body && n.nodeType === 1) {
        const ov = getComputedStyle(n).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
        n = n.parentElement;
    }
    return false;
};

const blockDrag = (el: any) => {
    if (!el || el._bd) return;
    el._bd = true;
    const pd = (e: Event) => { if (e.type === 'wheel' && isInScrollable(e.target)) return; e.preventDefault(); };
    el.addEventListener('mousedown', pd);
    el.addEventListener('touchstart', pd, { passive: false });
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('dragstart', pd);
};
const blockContentDrag = (el: any) => {
    if (!el || el._bcd) return;
    el._bcd = true;
    const pd = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('touchmove', pd, { passive: false });
};

// ─── 센서 등록 옵션 ────────────────────────────────────
const STATUS_OPTIONS = [
    { value: 0, label: '해당없음' },
    { value: 1, label: '욕창' },
    { value: 2, label: '욕창위험' },
    { value: 3, label: '낙상' },
    { value: 4, label: '요로감염' },
];
const CLASS_OPTIONS = [
    { value: '0', label: '일자형' },
    { value: '1', label: '삼각체결' },
];

const INITIAL_FORM = {
    NAME: '', GENDER: '', BIRTH: '', ADDRESS: '',
    GUARDIAN_NAME: '', GUARDIAN_PHONE: '', ROOM_NO: '', BED_NO: '',
    SENSOR_ID: '', PHONE: '', CLASSIFICATION: '0', DIAPER_CODE: '',
    STATUS: [] as number[],
};

// ─── 라디오 버튼 ────────────────────────────────────────
const RadioGroup = ({ options, value, onChange, T }: {
    options: { value: any; label: string }[]; value: any;
    onChange: (v: any) => void; T: Theme;
}) => {
    const { scale, s } = useScale();
    const mSt = useMemo(() => makeMSt(s), [scale]);
    return (
    <View style={{ flexDirection: 'row', gap: s(6), flexWrap: 'wrap', marginTop: s(4) }}>
        {options.map(o => {
            const active = o.value === value;
            return (
                <TouchableOpacity key={String(o.value)} onPress={() => onChange(o.value)}
                    style={[mSt.radioBtn, {
                        backgroundColor: active ? S.accent + '18' : T.cardInner,
                        borderColor: active ? S.accent : T.border,
                    }]}>
                    <View style={[mSt.radioDot, {
                        borderColor: active ? S.accent : T.muted,
                        backgroundColor: active ? S.accent : 'transparent',
                    }]} />
                    <Text style={{ color: active ? S.accent : T.text, fontSize: s(13), fontWeight: active ? '700' : '500' }}>{o.label}</Text>
                </TouchableOpacity>
            );
        })}
    </View>
    );
};

// ─── 체크박스 (다중선택, 해당없음 배타) ─────────────────
const CheckGroup = ({ options, values, onChange, T }: {
    options: { value: any; label: string }[]; values: any[];
    onChange: (v: any[]) => void; T: Theme;
}) => {
    const { scale, s } = useScale();
    const mSt = useMemo(() => makeMSt(s), [scale]);
    return (
    <View style={{ flexDirection: 'row', gap: s(6), flexWrap: 'wrap', marginTop: s(4) }}>
        {options.map(o => {
            const active = values.includes(o.value);
            return (
                <TouchableOpacity key={String(o.value)} onPress={() => {
                    if (active) {
                        onChange(values.filter(v => v !== o.value));
                    } else if (o.value === 0) {
                        onChange([0]);
                    } else {
                        onChange([...values.filter(v => v !== 0), o.value]);
                    }
                }}
                    style={[mSt.radioBtn, {
                        backgroundColor: active ? S.accent + '18' : T.cardInner,
                        borderColor: active ? S.accent : T.border,
                    }]}>
                    <View style={[mSt.radioDot, {
                        borderColor: active ? S.accent : T.muted,
                        backgroundColor: active ? S.accent : 'transparent',
                        borderRadius: s(3),
                    }]} />
                    <Text style={{ color: active ? S.accent : T.text, fontSize: s(13), fontWeight: active ? '700' : '500' }}>{o.label}</Text>
                </TouchableOpacity>
            );
        })}
    </View>
    );
};

// ─── 센서 등록 모달 ────────────────────────────────────
const AddSensorModal = ({ visible, onClose, repeaterId, area, roomNo, T }: {
    visible: boolean; onClose: (success?: boolean) => void;
    repeaterId: string; area: string; roomNo: string; T: Theme;
}) => {
    const { scale, s } = useScale();
    const mSt = useMemo(() => makeMSt(s), [scale]);
    const [form, setForm] = useState({ ...INITIAL_FORM, ROOM_NO: roomNo });
    const [submitting, setSubmitting] = useState(false);
    const [personList, setPersonList] = useState<any[]>([]);
    const [diaperList, setDiaperList] = useState<any[]>([]);
    const [personOpen, setPersonOpen] = useState(false);
    const [diaperOpen, setDiaperOpen] = useState(false);
    const [personSearch, setPersonSearch] = useState('');

    useEffect(() => {
        if (!visible) return;
        setForm({ ...INITIAL_FORM, ROOM_NO: roomNo });
        setPersonOpen(false);
        setDiaperOpen(false);
        setPersonSearch('');
        Data.fetchData('tabSensor/getPersonList', {}, (result: any, count: any) => {
            setPersonList(count > 0 ? result : []);
        }, true);
        Data.fetchData('tabSensor/getDiaperList', {}, (result: any, count: any) => {
            setDiaperList(count > 0 ? result.filter((d: any) => d.USE_YN === 0) : []);
        }, true);
    }, [visible, roomNo]);

    const set = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    const toDateStr = (v: any) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v).substring(0, 10);
        return d.toISOString().substring(0, 10);
    };

    const selectPerson = (person: any) => {
        setForm(prev => ({
            ...prev,
            NAME: person.NAME || '',
            BIRTH: toDateStr(person.BIRTH),
            GENDER: person.GENDER || '',
            ADDRESS: person.ADDRESS || '',
            PHONE: person.PHONE || '',
            GUARDIAN_NAME: person.GUARDIAN_NAME || '',
            GUARDIAN_PHONE: person.GUARDIAN_PHONE || '',
        }));
        setPersonOpen(false);
        setPersonSearch('');
    };

    const filteredPersons = personSearch
        ? personList.filter(p => (p.NAME || '').includes(personSearch))
        : personList;

    const handleSubmit = () => {
        if (!form.NAME.trim()) { alert('대상자를 선택해주세요.'); return; }
        if (!form.SENSOR_ID.trim()) { alert('센서 ID를 입력해주세요.'); return; }
        if (form.STATUS.length === 0) { alert('특이사항을 선택해주세요.'); return; }
        if (!form.DIAPER_CODE) { alert('기저귀 종류를 선택해주세요.'); return; }
        setSubmitting(true);
        Data.fetchData('tabSensor/insertSensor', {
            ...form,
            REPEATER_ID: repeaterId,
            AREA: area,
            STATUS_LIST: form.STATUS.length > 0
                ? form.STATUS.map(s => ({ STATUS: s, BIRTH: form.BIRTH }))
                : [{ STATUS: 0, BIRTH: form.BIRTH }],
        }, (result: any, count: any) => {
            setSubmitting(false);
            if (count === -1 && result?.message) {
                alert(result.message);
            } else {
                alert('센서가 등록되었습니다.');
                onClose(true);
            }
        }, true);
    };

    const selectedDiaper = diaperList.find(d => d.DIAPER_CODE === form.DIAPER_CODE);

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={mSt.overlay}>
                <View style={[mSt.container, { backgroundColor: T.card, borderColor: T.border }]}>
                    {/* 헤더 */}
                    <View style={[mSt.header, { borderBottomColor: T.border }]}>
                        <View>
                            <Text style={[mSt.title, { color: T.text }]}>센서 등록</Text>
                            <Text style={{ fontSize: s(12), color: T.muted, marginTop: s(2) }}>
                                중계기: {roomNo} · {repeaterId}
                            </Text>
                        </View>
                        <TouchableOpacity style={mSt.closeBtn} onPress={() => onClose()}>
                            <Ionicons name="close" size={s(22)} color={T.muted} />
                        </TouchableOpacity>
                    </View>

                    {/* 본문 */}
                    <ScrollView style={mSt.body} contentContainerStyle={{ gap: s(14), paddingBottom: s(24) }} showsVerticalScrollIndicator={false}>
                        {/* 대상자 선택 */}
                        <View style={[mSt.section, { borderColor: T.border }]}>
                            <Text style={[mSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="person-outline" size={s(14)} color={S.accent} /> 대상자 선택 *
                            </Text>
                            <TouchableOpacity
                                style={[mSt.dropdown, { backgroundColor: T.cardInner, borderColor: T.border }]}
                                onPress={() => { setPersonOpen(!personOpen); setDiaperOpen(false); }}
                            >
                                <Text style={{ color: form.NAME ? T.text : T.muted, fontSize: s(14) }}>
                                    {form.NAME ? `${form.NAME} (${form.GENDER || '-'}, ${form.BIRTH || '-'})` : '대상자를 선택하세요'}
                                </Text>
                                <Ionicons name={personOpen ? 'chevron-up' : 'chevron-down'} size={s(16)} color={T.muted} />
                            </TouchableOpacity>
                            {personOpen && (
                                <View style={[mSt.dropdownList, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                    <TextInput
                                        style={[mSt.searchInput, { backgroundColor: T.bg, borderColor: T.border, color: T.text }]}
                                        placeholder="이름 검색..."
                                        placeholderTextColor={T.muted}
                                        value={personSearch}
                                        onChangeText={setPersonSearch}
                                    />
                                    <ScrollView style={{ maxHeight: s(150) }} nestedScrollEnabled>
                                        {filteredPersons.length === 0 ? (
                                            <Text style={{ color: T.muted, fontSize: s(13), padding: s(10), textAlign: 'center' }}>결과 없음</Text>
                                        ) : filteredPersons.map((p, i) => (
                                            <TouchableOpacity key={i} onPress={() => selectPerson(p)}
                                                style={[mSt.dropdownItem, { borderBottomColor: T.border }]}>
                                                <Text style={{ color: T.text, fontSize: s(13), fontWeight: '600' }}>{p.NAME}</Text>
                                                <Text style={{ color: T.muted, fontSize: s(11) }}>{p.GENDER || '-'} · {toDateStr(p.BIRTH) || '-'}</Text>
                                            </TouchableOpacity>
                                        ))}
                                    </ScrollView>
                                </View>
                            )}
                        </View>

                        {/* 센서 정보 */}
                        <View style={[mSt.section, { borderColor: T.border }]}>
                            <Text style={[mSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="hardware-chip-outline" size={s(14)} color={S.accent} /> 센서 정보
                            </Text>
                            <View>
                                <Text style={[mSt.label, { color: T.textSub }]}>센서 ID *</Text>
                                <TextInput style={[mSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                    value={form.SENSOR_ID} onChangeText={v => set('SENSOR_ID', v)}
                                    placeholder="센서 ID 입력" placeholderTextColor={T.muted} />
                            </View>
                            <View style={{ flexDirection: 'row', gap: s(10) }}>
                                <View style={{ flex: 1 }}>
                                    <Text style={[mSt.label, { color: T.textSub }]}>호실</Text>
                                    <TextInput style={[mSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.ROOM_NO} onChangeText={v => set('ROOM_NO', v)}
                                        placeholder="호실" placeholderTextColor={T.muted} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={[mSt.label, { color: T.textSub }]}>침대번호</Text>
                                    <TextInput style={[mSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.BED_NO} onChangeText={v => set('BED_NO', v)}
                                        placeholder="침대번호" placeholderTextColor={T.muted} />
                                </View>
                            </View>
                        </View>

                        {/* 특이사항 */}
                        <View style={[mSt.section, { borderColor: T.border }]}>
                            <Text style={[mSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="alert-circle-outline" size={s(14)} color={S.replace} /> 특이사항 *
                            </Text>
                            <CheckGroup options={STATUS_OPTIONS} values={form.STATUS} onChange={v => set('STATUS', v)} T={T} />
                        </View>

                        {/* 기저귀 설정 */}
                        <View style={[mSt.section, { borderColor: T.border }]}>
                            <Text style={[mSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="options-outline" size={s(14)} color={S.accent} /> 기저귀 설정
                            </Text>
                            <View>
                                <Text style={[mSt.label, { color: T.textSub }]}>착용 방법</Text>
                                <RadioGroup options={CLASS_OPTIONS} value={form.CLASSIFICATION} onChange={v => set('CLASSIFICATION', v)} T={T} />
                            </View>
                            <View>
                                <Text style={[mSt.label, { color: T.textSub }]}>기저귀 종류 *</Text>
                                <TouchableOpacity
                                    style={[mSt.dropdown, { backgroundColor: T.cardInner, borderColor: T.border }]}
                                    onPress={() => { setDiaperOpen(!diaperOpen); setPersonOpen(false); }}
                                >
                                    <Text style={{ color: selectedDiaper ? T.text : T.muted, fontSize: s(14) }}>
                                        {selectedDiaper ? selectedDiaper.DIAPER_NAME : '선택하세요'}
                                    </Text>
                                    <Ionicons name={diaperOpen ? 'chevron-up' : 'chevron-down'} size={s(16)} color={T.muted} />
                                </TouchableOpacity>
                                {diaperOpen && (
                                    <View style={[mSt.dropdownList, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                        <ScrollView style={{ maxHeight: s(150) }} nestedScrollEnabled>
                                            {diaperList.map((d, i) => (
                                                <TouchableOpacity key={i}
                                                    onPress={() => { set('DIAPER_CODE', d.DIAPER_CODE); setDiaperOpen(false); }}
                                                    style={[mSt.dropdownItem, {
                                                        borderBottomColor: T.border,
                                                        backgroundColor: form.DIAPER_CODE === d.DIAPER_CODE ? S.accent + '12' : 'transparent',
                                                    }]}>
                                                    <Text style={{ color: form.DIAPER_CODE === d.DIAPER_CODE ? S.accent : T.text, fontSize: s(13), fontWeight: '600' }}>
                                                        {d.DIAPER_NAME}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </ScrollView>
                                    </View>
                                )}
                            </View>
                        </View>

                        {/* 보호자 정보 */}
                        <View style={[mSt.section, { borderColor: T.border }]}>
                            <Text style={[mSt.sectionTitle, { color: T.text }]}>
                                <Ionicons name="people-outline" size={s(14)} color={S.accent} /> 보호자 정보
                            </Text>
                            <View style={{ flexDirection: 'row', gap: s(10) }}>
                                <View style={{ flex: 1 }}>
                                    <Text style={[mSt.label, { color: T.textSub }]}>보호자 이름</Text>
                                    <TextInput style={[mSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.GUARDIAN_NAME} onChangeText={v => set('GUARDIAN_NAME', v)}
                                        placeholder="이름" placeholderTextColor={T.muted} />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <Text style={[mSt.label, { color: T.textSub }]}>보호자 연락처</Text>
                                    <TextInput style={[mSt.input, { backgroundColor: T.cardInner, borderColor: T.border, color: T.text }]}
                                        value={form.GUARDIAN_PHONE} onChangeText={v => set('GUARDIAN_PHONE', v)}
                                        placeholder="연락처" placeholderTextColor={T.muted} />
                                </View>
                            </View>
                        </View>
                    </ScrollView>

                    {/* 버튼 */}
                    <View style={[mSt.footer, { borderTopColor: T.border }]}>
                        <TouchableOpacity style={[mSt.btn, { backgroundColor: T.cardInner, borderColor: T.border }]} onPress={() => onClose()}>
                            <Text style={{ color: T.text, fontWeight: '600', fontSize: s(14) }}>취소</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[mSt.btn, { backgroundColor: S.accent, borderColor: S.accent, opacity: submitting ? 0.5 : 1 }]}
                            onPress={handleSubmit} disabled={submitting}
                        >
                            <Text style={{ color: '#fff', fontWeight: '700', fontSize: s(14) }}>{submitting ? '등록 중...' : '등록'}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

// ─── 메인 컴포넌트 ────────────────────────────────────

export default function RepeaterDetail({ navigation, route }: any) {
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const id = route?.params?.id;
            const key = `_navParams_RepeaterDetail_${id ?? ''}`;
            const saved = window.localStorage.getItem(key);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (route?.params?.onDeleted) parsed.onDeleted = route.params.onDeleted;
                return parsed;
            }
        } catch {}
        return route?.params || {};
    });
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [focus, setFocus] = useState(false);
    const onFocus = () => setFocus(!focus);
    const data = __p?.data;
    const [sensor, setSensor] = useState<any>([]);
    const [snPage, setSnPage] = useState(1);
    const [snPer, setSnPer] = useState(5);
    const _snTotal = Math.max(1, Math.ceil(((sensor) || []).length / snPer));
    const _snCur = Math.min(Math.max(1, snPage), _snTotal);
    const snData = ((sensor) || []).slice((_snCur - 1) * snPer, _snCur * snPer);
    const { dark, setDark } = useDarkMode();
    const [admin, setAdmin] = useState('1');
    const [deleteModal, setDeleteModal] = useState(false);
    const [addSensorModal, setAddSensorModal] = useState(false);
    const [sensorDeleteModal, setSensorDeleteModal] = useState(false);
    const [checkedItems, setCheckedItems] = useState<any[]>([]);

    const [area, setArea] = useState(data?.AREA || '');
    const [roomNo, setRoomNo] = useState(data?.ROOM_NO || '');
    const [repeaterID, setRepeaterID] = useState(data?.ID || '');
    const [IPv4, setIPV4] = useState(data?.IPV4 || '');
    const [version, setVersion] = useState(data?.VERSION || '');
    const [relVersion, setRelVersion] = useState(data?.RELEASE || '');

    useEffect(() => {
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        getStorage('AutoLogin').then((userInfo: any) => {
            if (userInfo?.admin) setAdmin(userInfo.admin);
        });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    // 네비게이션 헤더 스타일 (SensorDetail과 동일)
    useEffect(() => {
        navigation.setOptions({
            headerStyle: { backgroundColor: '#304156', elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
            headerTintColor: '#fff',
        });
    }, []);

    useEffect(() => {
        const anyModal = deleteModal || addSensorModal || sensorDeleteModal;
        if (!anyModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const prevent = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', prevent, { passive: false } as any);
        document.addEventListener('touchmove', prevent, { passive: false } as any);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
        };
    }, [deleteModal, addSensorModal, sensorDeleteModal]);

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    const getSensorList = () => {
        Data.fetchData('repeater/getSensorListByRepeater', {
            REPEATER_ID: data?.ID
        }, (result: any, count: any) => {
            console.log('getSensorListByRepeater ??', result);
            if (count > 0) {
                setSensor(result);
            } else {
                setSensor([]);
            }
        }, true);
    };

    useEffect(() => {
        getSensorList();
    }, [isFocused]);

    const onRepeaterUpdate = () => {
        Data.fetchData('repeater/updatRepeaterInfo', {
            IDX: data?.IDX, ID: repeaterID, AREA: area, IPV4: IPv4, ROOM_NO: roomNo, RELEASE: relVersion
        }, (result: any, count: any) => {
            if (count === 1) { alert('저장되었습니다.'); setFocus(false); }
            // 등록 화면(Repeater.tsx)과 같은 규약 — -2 호실 중복, -3 중계기 ID 중복
            else if (count === -2) alert('이미 등록된 병실 호수입니다.\n다른 호실명을 입력해 주세요.');
            else if (count === -3) alert('이미 등록된 중계기 ID입니다.');
            else alert('저장에 실패하였습니다.');
        }, true);
    };

    const onRepeaterDelete = () => {
        Data.fetchData('repeater/deleteRepeater', {
            IDX: data?.IDX, ID: repeaterID,
        }, (result: any, count: any) => {
            if (count === 1) {
                alert('삭제되었습니다.');
                if (route.params?.onDeleted) route.params.onDeleted(repeaterID);
                navigation.goBack();
            } else alert('삭제에 실패하였습니다.');
        }, true);
    };

    const onRepeaterUpgrade = () => {
        Data.fetchData('gwsensor/setRepeaterUpdateNeed', {
            RID: repeaterID,
        }, (result: any, count: any) => {
            if (count === 1) {
                alert(result.active === true ? '업데이트 요청을 보냈습니다.' : '업데이트 요청이 취소되었습니다.');
            } else alert('업데이트 요청에 실패하였습니다.');
        }, (error: any) => {
            alert('업그레이드 요청 중 오류가 발생했습니다.');
        });
    };

    const onSensorDelete = () => {
        let completed = 0;
        const total = checkedItems.length;
        const deletedIDXs = checkedItems.map((el: any) => el.IDX);
        checkedItems.forEach((item: any) => {
            Data.fetchData('sensor/deleteSensor', {
                IDX: item.IDX,
                SENSOR_ID: item.SENSOR_ID,
            }, (_: any, count: any) => {
                completed++;
                if (completed === total) {
                    setSensor((prev: any) => prev.filter((s: any) => !deletedIDXs.includes(s.IDX)));
                    setCheckedItems([]);
                    setSensorDeleteModal(false);
                    alert('삭제되었습니다.');
                }
            }, true);
        });
    };

    const isAdmin = admin === '0';

    // ─── 센서 테이블 컬럼 (체크박스 포함) ───
    const sensorColumnsWithCheck = [
        {
            id: 0,
            name: <Checkbox
                style={{ margin: s(3) }} color={S.accent}
                value={checkedItems.length > 0 && checkedItems.length === sensor?.length}
                onChange={(e: any) => {
                    if (e?.target?.checked) {
                        setCheckedItems(sensor.map((el: any) => ({ IDX: el.IDX, SENSOR_ID: el.SENSOR_ID })));
                    } else {
                        setCheckedItems([]);
                    }
                }}
            />,
            cell: (row: any) => {
                const checked = checkedItems.some((el: any) => el.IDX === row.IDX);
                return (
                    <Checkbox
                        style={{ margin: s(3) }} color={S.accent}
                        value={checked}
                        onChange={(e: any) => {
                            if (e?.target?.checked) {
                                setCheckedItems(prev => [...prev, { IDX: row.IDX, SENSOR_ID: row.SENSOR_ID }]);
                            } else {
                                setCheckedItems(prev => prev.filter((el: any) => el.IDX !== row.IDX));
                            }
                        }}
                    />
                );
            },
            grow: 0,
            width: '50px',
        },
        { id: 1, name: "이름", selector: (row: any) => row.NAME },
        { id: 2, name: '성별', selector: (row: any) => row.GENDER },
        { id: 3, name: '생년월일', selector: (row: any) => row.BIRTH ? moment(row.BIRTH).format('YYYY-MM-DD') : '' },
        { id: 4, name: '병실 호수', selector: (row: any) => row.ROOM_NO },
        { id: 5, name: '침대 no.', selector: (row: any) => row.BED_NO ? row.BED_NO : '' },
        { id: 6, name: '센서 ID', selector: (row: any) => row.SENSOR_ID },
        { id: 7, name: '연결 상태', selector: (row: any) => sensorState(row) },
    ];

    // 클립보드 복사
    const copyText = (t: any) => { try { (navigator as any).clipboard.writeText(String(t ?? '')); } catch (e) { } };
    // 값 인풋 스타일(편집모드에서만 테두리)
    const vInput = (extra?: any) => ({ fontSize: s(14), color: T.text, borderColor: focus ? S.accent : 'transparent', borderWidth: focus ? 1 : 0, borderRadius: s(6), paddingHorizontal: focus ? s(8) : 0, paddingVertical: focus ? s(4) : 0, backgroundColor: focus ? T.cardInner : 'transparent', outlineStyle: 'none', ...(extra || {}) });
    const connBadge = (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(5) }}>
            <View style={{ width: s(8), height: s(8), borderRadius: s(4), backgroundColor: data?.CONNECT == 0 ? S.ok : S.err }} />
            <Text style={{ fontSize: s(14), fontWeight: '600', color: data?.CONNECT == 0 ? S.ok : S.err }}>{data?.CONNECT == 0 ? '연결됨' : '연결끊김'}</Text>
        </View>
    );
    const copyable = (val: any, node: any) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(6) }}>
            <View style={{ flex: 1 }}>{node}</View>
            {!focus && <TouchableOpacity onPress={() => copyText(val)}><Ionicons name="copy-outline" size={s(15)} color={T.muted} /></TouchableOpacity>}
        </View>
    );
    // 2컬럼 키-값 그리드 데이터 (기획 슬라이드12: 정보 세로 표시)
    const infoRows: any[] = [
        [
            { label: '담당 구역', node: <TextInput autoCapitalize='none' editable={focus} style={vInput() as any} value={area} onChangeText={setArea} /> },
            { label: '병실 호수', node: <TextInput autoCapitalize='none' editable={focus} style={vInput() as any} value={roomNo} onChangeText={setRoomNo} /> },
        ],
        [
            { label: '중계기 ID', node: copyable(repeaterID, <TextInput autoCapitalize='none' editable={focus} style={vInput() as any} value={repeaterID} onChangeText={setRepeaterID} />) },
            { label: 'IPv4 주소', node: copyable(IPv4, <TextInput autoCapitalize='none' editable={focus} style={vInput() as any} value={IPv4} onChangeText={setIPV4} />) },
        ],
        [
            { label: '연결 상태', node: connBadge },
            { label: '현재 버전', node: <Text style={{ fontSize: s(14), color: T.text }}>{version}</Text> },
        ],
        [
            { label: 'REL 버전', node: <TextInput autoCapitalize='none' editable={focus} style={vInput({ color: S.purple, fontWeight: '600' }) as any} value={relVersion} onChangeText={setRelVersion} /> },
            null,
        ],
    ];

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            {/* ─── 중계기 상세 정보 카드 (2컬럼 키-값) ─── */}
            <View style={[styles.card, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* 헤더: 제목(구역+호실) + 상태 + 설명 + 버튼 */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: s(16) }}>
                    <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                            <Text style={{ fontSize: s(22), fontWeight: '800', color: T.text }}>{[area, roomNo].filter(Boolean).join(' · ')}</Text>
                            <View style={{ backgroundColor: (data?.CONNECT == 0 ? S.ok : S.err) + '20', paddingHorizontal: s(10), paddingVertical: s(3), borderRadius: s(20), flexDirection: 'row', alignItems: 'center', gap: s(5) }}>
                                <View style={{ width: s(6), height: s(6), borderRadius: s(3), backgroundColor: data?.CONNECT == 0 ? S.ok : S.err }} />
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: data?.CONNECT == 0 ? S.ok : S.err }}>{data?.CONNECT == 0 ? '연결됨' : '연결끊김'}</Text>
                            </View>
                        </View>
                        <Text style={{ fontSize: s(13), color: T.textSub, marginTop: s(6) }}>중계기 상세 정보와 상태를 확인합니다.</Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
                        {isAdmin && (
                            <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.purple }]} onPress={onRepeaterUpgrade}>
                                <Ionicons name="cloud-upload-outline" size={s(14)} color="#fff" style={{ marginRight: s(4) }} />
                                <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>센서 업데이트</Text>
                            </TouchableOpacity>
                        )}
                        {focus ? (
                            <>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.ok }]} onPress={onRepeaterUpdate}>
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>저장</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: T.muted }]} onPress={onFocus}>
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>취소</Text>
                                </TouchableOpacity>
                            </>
                        ) : (
                            <>
                                <TouchableOpacity style={[styles.headerBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: 'transparent' }]} onPress={() => copyText(repeaterID)}>
                                    <Ionicons name="copy-outline" size={s(13)} color={T.textSub} style={{ marginRight: s(4) }} />
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub }}>복사</Text>
                                </TouchableOpacity>
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={onFocus}>
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>수정</Text>
                                </TouchableOpacity>
                                {isAdmin && (
                                    <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.err }]} onPress={() => setDeleteModal(true)}>
                                        <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>삭제</Text>
                                    </TouchableOpacity>
                                )}
                            </>
                        )}
                    </View>
                </View>

                {/* 소제목 */}
                <Text style={{ fontSize: s(15), fontWeight: '700', color: T.text, marginBottom: s(10) }}>중계기 정보</Text>

                {/* 2컬럼 키-값 그리드 (기획 슬라이드12: 정보 세로 표시) */}
                <View style={{ borderWidth: 1, borderColor: T.border, borderRadius: s(10), overflow: 'hidden' }}>
                    {infoRows.map((row: any[], ri: number) => (
                        <View key={ri} style={{ flexDirection: 'row', borderBottomWidth: ri < infoRows.length - 1 ? 1 : 0, borderBottomColor: T.border }}>
                            {row.map((cell: any, ci: number) => (
                                cell ? (
                                    <View key={ci} style={{ flex: 1, flexDirection: 'row', alignItems: 'stretch', minHeight: s(46), borderRightWidth: ci === 0 ? 1 : 0, borderRightColor: T.border }}>
                                        <View style={{ width: s(100), paddingHorizontal: s(14), justifyContent: 'center', backgroundColor: T.cardInner, borderRightWidth: 1, borderRightColor: T.border }}>
                                            <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub }}>{cell.label}</Text>
                                        </View>
                                        <View style={{ flex: 1, paddingHorizontal: s(14), paddingVertical: s(8), justifyContent: 'center' }}>
                                            {cell.node}
                                        </View>
                                    </View>
                                ) : (
                                    <View key={ci} style={{ flex: 1, backgroundColor: T.card }} />
                                )
                            ))}
                        </View>
                    ))}
                </View>
            </View>

            {/* ─── 센서 리스트 카드 ─── */}
            <View style={[styles.card, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={styles.cardHeader}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>센서 리스트</Text>
                        {sensor?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{sensor.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
                        {checkedItems.length > 0 && (
                            <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.err }]} onPress={() => setSensorDeleteModal(true)}>
                                <Ionicons name="trash-outline" size={s(14)} color="#fff" style={{ marginRight: s(4) }} />
                                <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>삭제 ({checkedItems.length})</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={() => setAddSensorModal(true)}>
                            <Ionicons name="add" size={s(16)} color="#fff" style={{ marginRight: s(2) }} />
                            <Text style={{ fontSize: s(13), fontWeight: '600', color: '#fff' }}>센서 등록</Text>
                        </TouchableOpacity>
                    </View>
                </View>
                <DataTable
                    columns={sensorColumnsWithCheck}
                    data={snData}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(16), color: T.muted }}>연결된 센서가 없습니다.</Text>
                        </View>
                    }
                    onRowClicked={(row: any) => {
                        try { window.localStorage.setItem(`_navParams_SensorDetail_${row.SENSOR_ID}`, JSON.stringify({ data: row })); } catch {}
                        navigation.navigate('SensorDetail', { id: String(row.SENSOR_ID) });
                    }}
                />
                {(sensor || []).length > 0 && (
                    <TablePagination
                        rowCount={(sensor || []).length}
                        perPage={snPer}
                        page={_snCur}
                        onChangePage={setSnPage}
                        onChangePerPage={(n) => { setSnPer(n); setSnPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                        options={[5, 10, 15, 20]}
                    />
                )}
            </View>

            {/* ─── 중계기 삭제 확인 모달 ─── */}
            {deleteModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setDeleteModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                        <Ionicons name="warning-outline" size={s(36)} color={S.err} style={{ marginBottom: s(12) }} />
                        <Text style={{ fontSize: s(16), fontWeight: '600', color: T.text, textAlign: 'center' }}>
                            해당 중계기를 삭제하시겠습니까?
                        </Text>
                        <Text style={{ fontSize: s(12), color: S.err, marginTop: s(8) }}>이 작업은 되돌릴 수 없습니다.</Text>
                        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(12), marginTop: s(20) }}>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: S.err }]}
                                onPress={() => { setDeleteModal(false); onRepeaterDelete(); }}>
                                <Text style={styles.modalBtnText}>삭제</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setDeleteModal(false)}>
                                <Text style={[styles.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ─── 센서 삭제 확인 모달 ─── */}
            {sensorDeleteModal && createPortal(
                <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                    <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                        <TouchableOpacity activeOpacity={1} onPress={() => setSensorDeleteModal(false)} style={{ flex: 1 }} />
                    </View>
                    <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                        <Ionicons name="warning-outline" size={s(36)} color={S.err} style={{ marginBottom: s(12) }} />
                        <Text style={{ fontSize: s(16), fontWeight: '600', color: T.text, textAlign: 'center' }}>
                            선택한 센서 {checkedItems.length}개를 삭제하시겠습니까?
                        </Text>
                        <Text style={{ fontSize: s(12), color: S.err, marginTop: s(8) }}>이 작업은 되돌릴 수 없습니다.</Text>
                        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(12), marginTop: s(20) }}>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: S.err }]}
                                onPress={onSensorDelete}>
                                <Text style={styles.modalBtnText}>삭제</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={() => setSensorDeleteModal(false)}>
                                <Text style={[styles.modalBtnText, { color: T.textSub }]}>취소</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>,
                document.body
            )}

            {/* ─── 센서 등록 모달 ─── */}
            <AddSensorModal
                visible={addSensorModal}
                onClose={(success) => {
                    setAddSensorModal(false);
                    if (success) setTimeout(() => getSensorList(), 1000);
                }}
                repeaterId={repeaterID}
                area={area}
                roomNo={roomNo}
                T={T}
            />
        </View>
    );
}

// ─── 스타일 ─────────────────────────────────────────
const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: { flex: 1, padding: s(24) },
    card: {
        borderRadius: s(16), borderWidth: 0.5, overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: s(20),
        elevation: 3, paddingTop: s(20), paddingHorizontal: s(10), marginBottom: s(20),
    },
    cardHeader: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        paddingHorizontal: s(6), paddingBottom: s(14),
    },
    headerBtn: {
        flexDirection: 'row', alignItems: 'center', paddingHorizontal: s(14), height: s(34), borderRadius: s(10),
    },
    iconBtn: { width: s(34), height: s(34), borderRadius: s(10), justifyContent: 'center', alignItems: 'center' },
    infoTable: { borderWidth: 1, borderRadius: s(10), overflow: 'hidden', marginHorizontal: s(6), marginBottom: s(16) },
    infoRow: { flexDirection: 'row', minHeight: s(44) },
    infoCell: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: s(8), paddingVertical: s(6) },
    infoInput: { fontSize: s(13), textAlign: 'center', width: '90%', height: s(30), borderRadius: s(6), paddingHorizontal: s(6) },
    confirmModal: {
        alignSelf: 'center', width: s(340), borderRadius: s(16), borderWidth: 1, padding: s(30),
        alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15, shadowRadius: s(20), elevation: 10,
    },
    modalBtn: { paddingHorizontal: s(24), paddingVertical: s(10), borderRadius: s(10), alignItems: 'center', minWidth: s(90) },
    modalBtnText: { fontSize: s(14), fontWeight: '700', color: '#fff' },
});

// ─── 센서 등록 모달 스타일 ─────────────────────────────
const makeMSt = (s: ScaleFn) => StyleSheet.create({
    overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
    container:    { width: s(460), maxHeight: '88%', borderRadius: s(16), borderWidth: 0.5, overflow: 'hidden' },
    header:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: s(20), paddingVertical: s(16), borderBottomWidth: 1 },
    title:        { fontSize: s(18), fontWeight: '700' },
    closeBtn:     { width: s(32), height: s(32), borderRadius: s(8), alignItems: 'center', justifyContent: 'center' },
    body:         { paddingHorizontal: s(20), paddingTop: s(16) },
    section:      { borderWidth: 1, borderRadius: s(12), padding: s(14), gap: s(10) },
    sectionTitle: { fontSize: s(14), fontWeight: '700' },
    label:        { fontSize: s(12), fontWeight: '600', marginBottom: s(2) },
    input:        { borderWidth: 1, borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(8), fontSize: s(14) },
    dropdown:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(10) },
    dropdownList: { borderWidth: 1, borderRadius: s(8), marginTop: s(4), overflow: 'hidden' },
    dropdownItem: { paddingHorizontal: s(12), paddingVertical: s(10), borderBottomWidth: 1 },
    searchInput:  { margin: s(6), borderWidth: 1, borderRadius: s(6), paddingHorizontal: s(10), paddingVertical: s(6), fontSize: s(13) },
    radioBtn:     { flexDirection: 'row', alignItems: 'center', gap: s(6), paddingHorizontal: s(12), paddingVertical: s(7), borderRadius: s(8), borderWidth: 1 },
    radioDot:     { width: s(14), height: s(14), borderRadius: s(7), borderWidth: 2 },
    footer:       { flexDirection: 'row', gap: s(10), padding: s(16), borderTopWidth: 1, justifyContent: 'flex-end' },
    btn:          { paddingHorizontal: s(24), paddingVertical: s(10), borderRadius: s(8), borderWidth: 1 },
});
